import { useMsal } from '@azure/msal-react'
import {
  faArrowsRotate,
  faCalendarAlt,
  faCircleDollarToSlot,
  faInfoCircle,
  faLocationDot,
  faXmark,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ActionIcon,
  AspectRatio,
  Badge,
  Group,
  Image,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { useEffect, useState } from 'react'
import { fetchDeviceAdvertisementDetails } from '../../data/advertisement.service'
import { DeviceAdvertisementsForHost } from '../../models/DeviceAdvertisementsForHost'
import { TransactionItem } from '../../models/Transactions/TransactionItem'
import { COLORS, defaultImageUrl } from '../../utils/constants'
import { AdStatusTooltip } from './AdStatusTooltip'
import { formatNumber } from '../../utils/displayUtils'
import { useSessionInfo } from '../../contexts/SessionContext'
import { useLocalStorage } from '@mantine/hooks'

interface AdvertisementDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  advertisement: TransactionItem
}

export const AdvertisementDetailsModal = ({
  isOpen,
  onClose,
  advertisement,
}: AdvertisementDetailsModalProps) => {
  const { instance } = useMsal()
  const { session } = useSessionInfo()
  const [isHostMode] = useLocalStorage({
    key: 'isHostMode',
    defaultValue: false,
  })
  const [adDetails, setAdDetails] =
    useState<DeviceAdvertisementsForHost | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchDetails = async () => {
      if (!advertisement.deviceAdvertisementId) return
      setLoading(true)
      setError(null)
      try {
        const details = await fetchDeviceAdvertisementDetails(
          advertisement.deviceAdvertisementId,
          instance,
        )
        setAdDetails(details)
      } catch (err) {
        setError('Failed to load advertisement details')
        console.error('Error fetching advertisement details:', err)
      } finally {
        setLoading(false)
      }
    }

    if (isOpen) {
      fetchDetails()
    }
  }, [isOpen, advertisement.deviceAdvertisementId, instance])

  const getStatusColor = (status: string) => {
    return status === 'PAID' ? 'green' : 'yellow'
  }

  const getIntervalText = (
    numberOfDaysToRunAd: number | null,
    startDate: Date | null,
    endDate: Date | null,
  ) => {
    if (startDate && endDate) {
      const diffInTime = endDate.getTime() - startDate.getTime()
      const numDays = Math.floor(diffInTime / (1000 * 3600 * 24)) + 1
      return `${numDays} Days`
    }
    if (startDate) {
      return `1 Day`
    }

    switch (numberOfDaysToRunAd) {
      case 7:
        return '1 Week'
      case 14:
        return '2 Weeks'
      case 30:
        return '1 Month'
      case 90:
        return '3 Months'
      default:
        return `${numberOfDaysToRunAd} Days`
    }
  }

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      size='xl'
      centered
      withCloseButton={false}
      styles={{
        body: {
          padding: 0,
        },
      }}
    >
      <Paper p='xl'>
        <Group justify='space-between' mb='md'>
          <Title order={2}>{advertisement.name}</Title>
          <ActionIcon
            variant='subtle'
            onClick={onClose}
            color={COLORS.LightGreen}
            size='lg'
          >
            <FontAwesomeIcon icon={faXmark} />
          </ActionIcon>
        </Group>

        {loading ? (
          <Group justify='center' py='xl'>
            <Loader />
          </Group>
        ) : error ? (
          <Text c='red' ta='center' py='xl'>
            {error}
          </Text>
        ) : adDetails ? (
          <Group align='center' gap='xl'>
            <AspectRatio ratio={16 / 9} w={{ base: '100%', sm: '50%' }}>
              <Image
                src={adDetails.adImageUrl ?? defaultImageUrl}
                radius='md'
                alt={adDetails.organizationName}
              />
            </AspectRatio>
            <Stack style={{ flex: 1 }} gap='sm'>
              {/* Location Section */}
              <Group>
                <FontAwesomeIcon
                  icon={faLocationDot}
                  style={{ color: '#666', width: '16px' }}
                />
                <Text fw={500}>{adDetails.locationName}</Text>
              </Group>

              {/* Status and Amount Section */}
              <Stack gap='md'>
                <Group>
                  <FontAwesomeIcon
                    icon={faCircleDollarToSlot}
                    style={{ color: '#666', width: '16px' }}
                  />
                  <Text fw={500}>
                    Amount: ${formatNumber(advertisement.amount)}
                  </Text>
                  <AdStatusTooltip
                    status={advertisement.status}
                    isHostMode={isHostMode}
                  >
                    <Badge
                      color={getStatusColor(advertisement.status)}
                      size='sm'
                    >
                      {advertisement.status}
                    </Badge>
                  </AdStatusTooltip>
                </Group>
                {adDetails.priceSnapshot !== undefined && !advertisement.isAllLocations && (
                  <Group>
                    <FontAwesomeIcon
                      icon={faCircleDollarToSlot}
                      style={{ color: '#666', width: '16px' }}
                    />
                    <Text fw={500}>
                      Price Charged: ${formatNumber(adDetails.priceSnapshot)}{' '}
                      for{' '}
                      {getIntervalText(
                        adDetails.numberOfDaysToRunAd,
                        adDetails.startDateLocal
                          ? new Date(adDetails.startDateLocal)
                          : null,
                        adDetails.endDateLocal
                          ? new Date(adDetails.endDateLocal)
                          : null,
                      )}
                    </Text>
                  </Group>
                )}
              </Stack>

              {/* Advertisement Run Dates Section */}
              <Stack gap='md'>
                {adDetails.startDateLocal && adDetails.endDateLocal && (
                  <Group>
                    <FontAwesomeIcon
                      icon={faCalendarAlt}
                      style={{ color: '#666', width: '16px' }}
                    />
                    <Text>
                      Run Dates:{' '}
                      {new Date(adDetails.startDateLocal)?.toLocaleDateString()}{' '}
                      - {new Date(adDetails.endDateLocal)?.toLocaleDateString()}
                    </Text>
                  </Group>
                )}

                {!adDetails.startDateLocal &&
                  !adDetails.endDateLocal &&
                  adDetails.numberOfDaysToRunAd && (
                    <Group>
                      <FontAwesomeIcon
                        icon={faCalendarAlt}
                        style={{ color: '#666', width: '16px' }}
                      />
                      <Text>
                        Duration: {adDetails.numberOfDaysToRunAd} days
                      </Text>
                    </Group>
                  )}
              </Stack>

              {/* Additional Information Section */}
              <Stack gap='md'>
                {advertisement.isAutoRenewal && (
                  <Group>
                    <FontAwesomeIcon
                      icon={faArrowsRotate}
                      style={{ color: '#666', width: '16px' }}
                    />
                    <Text>Auto-Renewal: Enabled</Text>
                  </Group>
                )}

                {advertisement.isDesignRequest && (
                  <Group>
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      style={{ color: '#666', width: '16px' }}
                    />
                    <Text>Type: Design Request</Text>
                  </Group>
                )}
              </Stack>
            </Stack>
          </Group>
        ) : null}
      </Paper>
    </Modal>
  )
}
